<template>
  <component
    :is="rootTag"
    :class="classes"
    :href="rootHref"
    :target="rootHref ? '_blank' : undefined"
    :rel="rootHref ? 'noopener noreferrer' : undefined"
    :aria-disabled="condition.user_satisfied ? undefined : 'false'"
    @click="onClick"
  >
    <header :class="$style.head">
      <span :class="$style.indexPill">{{ t('index', { n: index }) }}</span>
      <span :class="$style.headRight">
        <ui-icon
          v-if="condition.user_satisfied"
          name="check"
          :class="$style.statusIconDone"
          :aria-label="t('done')"
        />
        <ui-icon
          v-else-if="condition.action_url"
          name="external-link"
          :class="$style.statusIconLink"
          :aria-label="t('open')"
        />
      </span>
    </header>

    <p :class="$style.label">{{ condition.display_label }}</p>
    <p v-if="condition.description" :class="$style.description">
      {{ condition.description }}
    </p>
  </component>
</template>

<i18n lang="yaml">
ru:
  index: 'Условие {n}'
  done: Выполнено
  open: Открыть
</i18n>

<script lang="ts" setup>
import { computed } from 'vue'
import UiIcon from '@/ui-core/components/icon/index.vue'
import type { Stage, StageUnlock, UnlockCondition } from '../../../types'

defineOptions({ name: 'PickemUnlockConditionItem' })

const props = defineProps<{
  condition: UnlockCondition
  index: number
  stage: Stage
  unlock: StageUnlock
}>()

const cssModule = useCssModule()
const { t } = useI18n()

// Если у условия есть action_url — карточка кликабельна и должна
// открываться в новой вкладке (юзер «уходит выполнять» условие). Без
// action_url — это статичная карточка-описание, чтобы юзер просто видел
// требования (например, «достичь N уровня внутри приложения»).
const rootTag = computed(() => (props.condition.action_url ? 'a' : 'div'))
const rootHref = computed(() =>
  props.condition.action_url && !props.condition.user_satisfied
    ? props.condition.action_url
    : undefined
)

const classes = computed(() => [
  cssModule.item,
  props.condition.user_satisfied && cssModule.satisfied,
  props.condition.action_url && cssModule.clickable
])

const emit = defineEmits<{
  open: [condition: UnlockCondition, index: number]
}>()

const onClick = () => {
  if (!props.condition.action_url || props.condition.user_satisfied) return
  emit('open', props.condition, props.index)
}
</script>

<style lang="scss" module>
.item {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  height: 100%;
  gap: 0.8em;
  padding: 1.6em;
  border-radius: 1.2em;
  background-color: color-get(--root-rgb-bg-card);
  border: none;
  color: color-get(--root-rgb-text-1);
  text-decoration: none;
  transition: opacity 0.15s ease;
}

.clickable {
  cursor: pointer;

  @include hover-focus {
    opacity: 0.88;
  }
}

.satisfied {
  cursor: default;
}

.head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.8em;
}

.indexPill {
  display: inline-flex;
  align-items: center;
  padding: 0.4em 0.8em;
  border-radius: 0.4em;
  background-color: color-get(--root-rgb-bg-tone-dark);
  color: color-get(--root-rgb-accent-4);
  @include typo-get(body-xs);
}

.headRight {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.statusIconLink {
  width: 1.6em;
  height: 1.6em;
  color: color-get(--root-rgb-text-2);
}

.statusIconDone {
  width: 1.6em;
  height: 1.6em;
  color: color-get(--root-rgb-accent-2);
}

.label {
  margin: 0;
  @include typo-get(subhead-m);
  color: color-get(--root-rgb-text-1);
}

.description {
  margin: 0;
  @include typo-get(body-s);
  color: color-get(--root-rgb-text-2);
}
</style>
